/**
 * AI email draft review panel (SA-99). Sits above the composer for email
 * conversations: shows the pending AI-generated reply for staff to edit,
 * regenerate, discard, or send. Follows the visual idiom of the
 * escalation/bank-transfer decision cards in InboxConversationDetail.tsx.
 *
 * SA-1040 PR 2: an offer email (a draft with `quote`) shows its quotation, sends the hash of the
 * file it showed with every save and with Send, and has no Regenerate. A save refused because the
 * quotation moved is worded, and the panel then takes the reworded draft (the person's typing over
 * a stale panel is dropped, by ruling).
 *
 * SA-1215: Send clears a refused save's error; a rewording that replaces what the person typed says
 * so (`REWORDED`); the download error clears when the draft or its quotation moves; and an offer
 * email signed by someone else names them (`signedAs`) above the text.
 */
import { useEffect, useState } from 'react'
import { Paperclip } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Spinner } from '@/components/ui'
import { cn } from '@/utils'
import { useEmailDraft } from '@/hooks/useEmailDraft'
import { downloadQuoteDocument, recordsKey } from '@/hooks/useRecordExtras'
import * as text from './emailDraftText'

interface EmailDraftPanelProps {
  conversationId: string
  /** Whether this mailbox is authorized to send outbound email. */
  sendAuthorized: boolean
  /** Called after a successful send. */
  onSent?: () => void
  /** SA-1347: the pane's grid placement when the panel sits beside the thread. */
  className?: string
}

type Field = 'subject' | 'body'

/** The draft the panel last took in, and per box the values this panel saved there since. */
interface Seed {
  id: string
  subject: string
  body: string
  sha: string | null
  saved: Record<Field, string[]>
}

/** What one box does when the fetched draft arrives (SA-1266): it refills when its server text moved,
 *  unless the new text is a value this panel saved there, which is that save coming back: the box keeps
 *  what the person has typed since, and that save and the earlier ones are dropped. */
function refill(seeded: string, incoming: string, saved: string[]): { fills: boolean; saved: string[] } {
  if (incoming === seeded) return { fills: false, saved }
  const at = saved.indexOf(incoming)
  if (at >= 0) return { fills: false, saved: saved.slice(at + 1) }
  return { fills: true, saved: [] }
}

export function EmailDraftPanel({ conversationId, sendAuthorized, onSent, className }: EmailDraftPanelProps) {
  const { draft, isLoading, isError, error, update, regenerate, send, discard } =
    useEmailDraft(conversationId, true)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const qc = useQueryClient()
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [reworded, setReworded] = useState(false)
  const [seed, setSeed] = useState<Seed | null>(null)
  const quoteSha = draft?.quote?.document_sha256
  useEffect(() => { setDownloadError(null) }, [draft?.id, quoteSha])

  // Seed/reset local edits whenever the fetched draft's content changes — covers the initial load,
  // a Regenerate replacing the body and an edit of the quotation rewording it. The seed is set
  // during the render, never in an effect (SA-1265): an effect reseeds a render later, and in that
  // committed render the blur and Send handlers held the old text beside the new draft's hash, so a
  // save or a send could carry the old wording past the server's hash check. React re-runs a
  // component that updates its own state while rendering before it commits anything. The seed is
  // keyed on content, never the fetched object, so a hash-only change keeps the person's typing.
  // Each box refills on its own (SA-1266): only a box whose server text moved, so a blur save of one
  // box coming back never resets the other, and never a box whose server text is a value this panel
  // itself saved there (its save coming back), so typing done since that save stays.
  if (draft && (seed === null || seed.id !== draft.id)) {
    setReworded(false)
    setSeed({ id: draft.id, subject: draft.subject, body: draft.body_text,
      sha: draft.quote?.document_sha256 ?? null, saved: { subject: [], body: [] } })
    setSubject(draft.subject)
    setBody(draft.body_text)
  } else if (draft && seed && (seed.subject !== draft.subject || seed.body !== draft.body_text)) {
    const sha = draft.quote?.document_sha256 ?? null
    const subjectNext = refill(seed.subject, draft.subject, seed.saved.subject)
    const bodyNext = refill(seed.body, draft.body_text, seed.saved.body)
    if (draft.edited_by_user_id == null && seed.sha !== sha) {
      // SA-1215: the same draft, reworded by an edit of the quotation, replaces what the person
      // typed (SA-1040's ruling); say why. The server rewords only a draft nobody edited
      // (`retemplate`), so a seed is a rewording only of such a draft, judged against the last
      // seed's own hash. Only typing this seed is about to throw away counts: a box it refills
      // that was typed in since the last seed and that the new text does not already hold.
      const replaced = (fills: boolean, local: string, seeded: string, incoming: string) =>
        fills && local !== seeded && local !== incoming
      if (replaced(subjectNext.fills, subject, seed.subject, draft.subject)
          || replaced(bodyNext.fills, body, seed.body, draft.body_text)) {
        setReworded(true)
      }
    }
    setSeed({ id: draft.id, subject: draft.subject, body: draft.body_text, sha,
      saved: { subject: subjectNext.saved, body: bodyNext.saved } })
    if (subjectNext.fills) setSubject(draft.subject)
    if (bodyNext.fills) setBody(draft.body_text)
  }

  const activeError = (send.error ?? regenerate.error ?? discard.error ?? update.error) as
    | Error
    | null

  if (isLoading) return null

  // A real fetch failure (auth/500/network) — distinct from "no pending
  // draft" (query resolves to null on a clean 404). Surface it instead of
  // silently falling through to the "Generate AI draft" empty state.
  if (isError) {
    return (
      <div className={cn('border-t border-border px-4 py-3', className)}>
        <p className="text-xs text-destructive" role="alert">
          {(error as Error)?.message || 'Could not load the email draft.'}
        </p>
      </div>
    )
  }

  if (!draft) {
    if (!sendAuthorized) return null
    return (
      <div className={cn('border-t border-border px-4 py-3', className)}>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => regenerate.mutate()}
          disabled={regenerate.isPending}
          className="text-xs"
        >
          {regenerate.isPending && <Spinner className="w-3 h-3 mr-1" />}
          Generate AI draft
        </Button>
        {activeError && (
          <p className="text-xs text-destructive mt-2" role="alert">
            {text.sendErrorText(activeError)}
          </p>
        )}
      </div>
    )
  }

  // A `sending` draft means the prior send is unresolved (its outcome with
  // the provider is unknown) — the panel goes read-only and only offers a
  // provider-verifying retry, never silent re-editing. Set by the server;
  // never derived from `send.isPending` (that's this tab's own in-flight
  // request, not the draft's durable state).
  const isSending = draft.status === 'sending'

  // SA-1040 PR 2: an offer email carries its version's quotation; it is not the AI's, so it has no Regenerate.
  const quote = draft.quote ?? null
  // The hash rides every write of an offer email's text, so a save or a send from a stale panel is
  // refused rather than written over the draft an edit of the quote reworded.
  const pin = quote?.document_sha256 ? { quote_document_sha256: quote.document_sha256 } : {}
  // Each write of a box's text notes the value, so the refill above knows that save when it comes back.
  // A value equal to the stored text is noted too: an earlier save still out may move the stored text
  // first, and this one then comes back as a change.
  const remember = (field: Field, value: string) => setSeed((s) => {
    if (s === null || s.id !== draft.id) return s
    const sent = s.saved[field]
    if (sent[sent.length - 1] === value) return s
    return { ...s, saved: { ...s.saved, [field]: [...sent, value] } }
  })
  const handleDownload = async () => {
    if (!quote?.document_name) return
    setDownloadError(null)
    try { await downloadQuoteDocument(quote.order_id, quote.version_number, quote.document_name) } catch { setDownloadError(text.DOWNLOAD_FAILED) }
  }

  return (
    <div
      className={cn('border-t border-border px-4 py-3 bg-muted/30', className)}
      role="region"
      aria-label="email draft"
    >
      <p className="text-xs text-muted-foreground font-medium mb-2">
        {quote ? text.OFFER_HEADING : text.AI_HEADING}
      </p>
      {isSending && (
        <p className="text-xs text-muted-foreground mb-2" role="status">
          This send is unresolved — the message may or may not have reached
          the mail provider. Retry send checks with the provider before
          sending again, so it will not duplicate the email.
        </p>
      )}
      {quote && (
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="draft-attachment">
          <Paperclip className="h-3 w-3" aria-hidden />
          <span>{text.attachmentLine(quote)}</span>
          {quote.document_name && (
            <Button size="sm" variant="secondary" className="text-xs" onClick={() => void handleDownload()}>{text.DOWNLOAD}</Button>
          )}
        </div>
      )}
      {downloadError && <p className="text-xs text-destructive mb-2" role="alert">{downloadError}</p>}
      {quote && draft.signed_by_name && !draft.signed_by_viewer && (
        <p className="text-xs text-muted-foreground mb-2">{text.signedAs(draft.signed_by_name)}</p>
      )}
      {/* Always mounted and never `display: none`, so the live region is in the tree when a refetch
          fills it (the SaveRow precedent); empty, it takes no room, and the margin comes with the text. */}
      <p className={`text-xs text-muted-foreground ${reworded ? 'mb-2' : ''}`} role="status">{reworded && text.REWORDED}</p>
      <div className="flex flex-col gap-2">
        <input
          aria-label="Draft subject"
          value={subject}
          onChange={(e) => { setSubject(e.target.value); setReworded(false) }}
          onBlur={() => { remember('subject', subject); update.mutate({ subject, ...pin }) }}
          disabled={isSending}
          className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring disabled:opacity-50"
        />
        <textarea
          aria-label="Draft body"
          value={body}
          onChange={(e) => { setBody(e.target.value); setReworded(false) }}
          onBlur={() => { remember('body', body); update.mutate({ body_text: body, ...pin }) }}
          rows={4}
          disabled={isSending}
          className="w-full resize-none rounded-md border border-border bg-card px-2 py-1.5 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring disabled:opacity-50"
        />
      </div>
      {activeError && (
        <p className="text-xs text-destructive mt-2" role="alert">
          {text.sendErrorText(activeError)}
        </p>
      )}
      <div className="flex items-center gap-2 mt-2">
        <Button
          size="sm"
          onClick={() => {
            // A blur save refused earlier must not leave its error under an email that went out.
            update.reset()
            remember('subject', subject)
            remember('body', body)
            send.mutate(
              { draft_id: draft.id, subject, body_text: body, ...pin },
              {
                onSuccess: () => {
                  if (quote) void qc.invalidateQueries({ queryKey: [...recordsKey, quote.order_id, 'quotes'] })
                  onSent?.()
                },
              },
            )
          }}
          disabled={send.isPending}
          className="text-xs"
        >
          {send.isPending && <Spinner className="w-3 h-3 mr-1" />}
          {isSending ? 'Retry send' : 'Send'}
        </Button>
        {!quote && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => regenerate.mutate()}
            disabled={regenerate.isPending || isSending}
            className="text-xs"
          >
            {regenerate.isPending && <Spinner className="w-3 h-3 mr-1" />}
            Regenerate
          </Button>
        )}
        <Button
          size="sm"
          variant="secondary"
          onClick={() => discard.mutate()}
          disabled={discard.isPending || isSending}
          className="text-xs text-destructive hover:text-destructive/80"
        >
          {discard.isPending && <Spinner className="w-3 h-3 mr-1" />}
          Discard
        </Button>
      </div>
    </div>
  )
}
